import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {createSyncQueue} from '../offline/queue.js';
import {createRecipeSave,recipeSaveId} from './recipe-save.js';

export const RECIPE_SAVE_ENTITY_TYPE='recipe_save';

function required(value,label,max=160){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function clean(row){
  const copy={...row};
  delete copy.key;
  delete copy.entityType;
  return copy;
}

export function createRecipeSaveStore({
  repository,
  userId,
  now=()=>new Date().toISOString()
}={}){
  if(!repository?.get||!repository?.list||!repository?.put||!repository?.remove||!repository?.listPending){
    throw new TypeError('repository is required');
  }
  const owner=required(userId,'userId',128);
  const queue=createSyncQueue({repository,now});

  async function get(recipeId){
    const id=recipeSaveId(owner,required(recipeId,'recipeId',128));
    const row=await repository.get(RECIPE_SAVE_ENTITY_TYPE,id);
    if(!row||row.userId!==owner)return null;
    return Object.freeze(clean(row));
  }

  async function list({includeDeleted=false}={}){
    const rows=await repository.list(RECIPE_SAVE_ENTITY_TYPE);
    return Object.freeze((rows||[])
      .filter(row=>row.userId===owner)
      .filter(row=>includeDeleted||!row.deletedAt)
      .sort((a,b)=>String(b.savedAt||'').localeCompare(String(a.savedAt||''))||String(a.recipeId).localeCompare(String(b.recipeId)))
      .map(row=>Object.freeze(clean(row))));
  }

  async function save(recipeId,savedRecipeVersion){
    const recipe=required(recipeId,'recipeId',128);
    const existing=await get(recipe);
    const savedAt=existing?.savedAt||now();
    const domain=createRecipeSave({
      userId:owner,
      recipeId:recipe,
      savedRecipeVersion,
      savedAt,
      updatedAt:now()
    });
    const entity={
      ...(existing||createEntityMeta({id:domain.id,now:savedAt})),
      ...domain,
      id:domain.id,
      userId:owner,
      deletedAt:null,
      updatedAt:now()
    };
    const operation=existing&&Number(existing.serverVersion)>0?'update':'create';
    return queue.enqueue(RECIPE_SAVE_ENTITY_TYPE,entity,operation);
  }

  async function remove(recipeId){
    const existing=await get(recipeId);
    if(!existing)return null;
    if(existing.syncStatus===SyncStatus.PENDING_CREATE){
      await repository.remove(RECIPE_SAVE_ENTITY_TYPE,existing.id);
      return Object.freeze({...existing,removedLocally:true});
    }
    return queue.enqueue(RECIPE_SAVE_ENTITY_TYPE,existing,'delete');
  }

  async function toggle(recipeId,savedRecipeVersion){
    const existing=await get(recipeId);
    if(existing&&!existing.deletedAt)return Object.freeze({saved:false,entity:await remove(recipeId)});
    return Object.freeze({saved:true,entity:await save(recipeId,savedRecipeVersion)});
  }

  async function replaceFromRemote(remoteRows=[]){
    const remoteById=new Map((remoteRows||[]).map(row=>[recipeSaveId(owner,row.recipeId),row]));
    const local=await list({includeDeleted:true});

    for(const row of local){
      if(row.syncStatus!==SyncStatus.SYNCED)continue;

      if(row.deletedAt){
        if(remoteById.has(row.id)){
          // A just-acknowledged delete may still be visible from an eventually
          // consistent/stale remote read. The local tombstone is the barrier:
          // never turn that stale row back into an active save.
          remoteById.delete(row.id);
          continue;
        }

        // Remote absence is the confirmation that makes it safe to compact the
        // acknowledged local tombstone.
        await repository.remove(RECIPE_SAVE_ENTITY_TYPE,row.id);
        continue;
      }

      if(!remoteById.has(row.id))await repository.remove(RECIPE_SAVE_ENTITY_TYPE,row.id);
    }

    for(const remote of remoteById.values()){
      const id=recipeSaveId(owner,remote.recipeId);
      const current=await repository.get(RECIPE_SAVE_ENTITY_TYPE,id);
      if(current&&current.syncStatus!==SyncStatus.SYNCED)continue;
      const domain=createRecipeSave({
        userId:owner,
        recipeId:remote.recipeId,
        savedRecipeVersion:remote.savedRecipeVersion,
        savedAt:remote.savedAt,
        updatedAt:remote.updatedAt
      });
      await repository.put(RECIPE_SAVE_ENTITY_TYPE,{
        ...domain,
        id,
        createdAt:domain.savedAt,
        deletedAt:null,
        syncStatus:SyncStatus.SYNCED,
        serverVersion:Number(remote.serverVersion)||1,
        syncAttempts:0,
        lastSyncAttemptAt:null,
        lastSyncFailureAt:null,
        lastSyncErrorCode:null,
        conflict:null
      });
    }
    return list();
  }

  return Object.freeze({userId:owner,queue,get,list,save,remove,toggle,replaceFromRemote});
}
